import { z } from 'zod'
import { PaginationQuerySchema, paginated } from './common'
import { IsoDateTimeSchema, OkResponseSchema, UuidSchema } from '../primitives'
import { route } from '../route-def'
import { MemberMessageSchema, ReviewMediaKindSchema, SignInModeSchema } from './member'
import { DeviceDiagnosticsSchema, DeviceMeasureSchema, LocationVerdictSchema } from './location'
import {
  CheckStateSchema,
  DOCUMENT_EXEMPTION_STATES,
  DocumentCheckSchema,
  ManualVerificationSchema,
} from '../domain/document-policy'
import { NameScriptSchema } from './documents'
import { RegistrationStageSchema, RegistrationStatusValueSchema } from './registration'
import { LOCATION_LEVELS } from '../domain/location-policy'

/**
 * Admin features over the API (spec "Admin features", E9, P5; Task 17): members list and search, review queue,
 * member detail (biometric and location panels, media, actions), review actions, map points, messages inbox,
 * blocklist, audit log, overview and the provider comparison. Every route is admin-only. Provider ids are plain
 * strings (never an enum); an id the registry no longer has renders as "<id> (removed)".
 */

const nullableDate = IsoDateTimeSchema.nullable()

export const ADMIN_MEMBER_STATUS_FILTERS = [
  'approved',
  'review',
  'rejected',
  'blocked_duplicate',
  'retry_allowed',
  'erased',
  /** Not decided yet (stage before `decided`). */
  'in_progress',
] as const
export const AdminMemberStatusFilterSchema = z.enum(ADMIN_MEMBER_STATUS_FILTERS)

const boolQuery = z.enum(['true', 'false']).transform((v) => v === 'true')

export const AdminMembersQuerySchema = PaginationQuerySchema.extend({
  /** Name, email or city; case- and accent-insensitive. */
  q: z.string().trim().max(120).optional(),
  status: AdminMemberStatusFilterSchema.optional(),
  provider: z.string().max(32).optional(),
  groupId: UuidSchema.optional(),
  divergence: boolQuery.optional(),
})
export type AdminMembersQuery = z.input<typeof AdminMembersQuerySchema>

export const AdminCitySchema = z.object({
  name: z.string(),
  admin1: z.string().nullable(),
  country: z.string(),
})

export const AdminMemberListItemSchema = z.object({
  id: UuidSchema,
  name: z.string().nullable(),
  email: z.string().nullable(),
  stage: RegistrationStageSchema,
  status: RegistrationStatusValueSchema.nullable(),
  provider: z.string(),
  /** displayName, or "<id> (removed)". */
  providerName: z.string(),
  city: AdminCitySchema.nullable(),
  locationDivergence: z.boolean().nullable(),
  divergenceKm: z.number().nullable(),
  reviewReasons: z.array(z.string()),
  groupIds: z.array(UuidSchema),
  createdAt: IsoDateTimeSchema,
  decidedAt: nullableDate,
})
export type AdminMemberListItem = z.infer<typeof AdminMemberListItemSchema>

export const AdminMembersResponseSchema = paginated(AdminMemberListItemSchema).extend({
  /** Registrations not finished yet (the `in_progress` filter), whatever the other filters: the toggle's count. */
  inProgressCount: z.number().int().min(0),
})
export type AdminMembersResponse = z.infer<typeof AdminMembersResponseSchema>

export const AdminReviewItemSchema = z.object({
  id: UuidSchema,
  name: z.string().nullable(),
  email: z.string().nullable(),
  provider: z.string(),
  providerName: z.string(),
  city: AdminCitySchema.nullable(),
  /** Reason chips. */
  reviewReasons: z.array(z.string()),
  decidedAt: nullableDate,
  hasTemplate: z.boolean(),
})
export const AdminReviewResponseSchema = z.object({ items: z.array(AdminReviewItemSchema) })
export type AdminReviewResponse = z.infer<typeof AdminReviewResponseSchema>

/**
 * Discarded members (R112): registrations an admin no longer works with in the main list, a duplicate
 * (`blocked_duplicate`) or one an admin did not accept (`rejected`). The Members list leaves them out unless its
 * status filter asks for them; the Discarded page lists them with the reason and, for a duplicate, the matched member.
 */
export const ADMIN_DISCARDED_STATUSES = ['blocked_duplicate', 'rejected'] as const
export const ADMIN_DISCARD_REASONS = ['duplicate', 'not_accepted'] as const
export type AdminDiscardReason = (typeof ADMIN_DISCARD_REASONS)[number]

export const AdminDiscardedQuerySchema = PaginationQuerySchema.extend({
  /** Name, email or city; case- and accent-insensitive. */
  q: z.string().trim().max(120).optional(),
  reason: z.enum(ADMIN_DISCARD_REASONS).optional(),
  provider: z.string().max(32).optional(),
})
export type AdminDiscardedQuery = z.input<typeof AdminDiscardedQuerySchema>

export const AdminDiscardedItemSchema = AdminMemberListItemSchema.extend({
  reason: z.enum(ADMIN_DISCARD_REASONS),
  /** The admin's reason category of a reject (age, place, face, other). */
  rejectReason: z.string().nullable(),
  /** A duplicate: the member whose face it matched (null once that member is erased). */
  matchedMember: z.object({ id: UuidSchema, name: z.string().nullable(), status: z.string().nullable() }).nullable(),
})
export type AdminDiscardedItem = z.infer<typeof AdminDiscardedItemSchema>

export const AdminDiscardedResponseSchema = paginated(AdminDiscardedItemSchema)
export type AdminDiscardedResponse = z.infer<typeof AdminDiscardedResponseSchema>

/* ------------------------------------------------------------------ member detail */

const MemberLinkSchema = z.object({
  id: UuidSchema,
  name: z.string().nullable(),
  status: z.string().nullable(),
  similarity: z.number().nullable(),
})

export const AdminAttemptSchema = z.object({
  id: UuidSchema,
  provider: z.string(),
  providerName: z.string(),
  status: z.string(),
  counted: z.boolean(),
  outcome: z.string().nullable(),
  providerDecision: z.string().nullable(),
  livenessScore: z.number().nullable(),
  livenessMethod: z.string().nullable(),
  warningCodes: z.array(z.string()),
  rawCodes: z.array(z.string()),
  reviewReasons: z.array(z.string()),
  ageProvider: z.number().nullable(),
  ageOwn: z.number().nullable(),
  challengeRequested: z.string().nullable(),
  /** Own capture: token issue → upload received, in ms. */
  captureMs: z.number().nullable(),
  createdAt: IsoDateTimeSchema,
  decidedAt: nullableDate,
})
export type AdminAttempt = z.infer<typeof AdminAttemptSchema>

export const AdminBiometricPanelSchema = z.object({
  provider: z.string(),
  providerName: z.string(),
  providerDecision: z.string().nullable(),
  /** null = "provider gives no score". */
  livenessScore: z.number().nullable(),
  livenessMethod: z.string().nullable(),
  warnings: z.array(z.string()),
  age: z.object({ years: z.number().nullable(), source: z.enum(['provider', 'own']), band: z.string() }).nullable(),
  ageOwn: z.number().nullable(),
  uniqueness: z
    .object({
      band: z.enum(['unique', 'borderline', 'duplicate']),
      similarity: z.number().nullable(),
      matched: MemberLinkSchema.nullable(),
      /** Near-tie (preflight F18): the second candidate. */
      second: MemberLinkSchema.nullable(),
    })
    .nullable(),
  attemptsUsed: z.number().int().min(0),
  attemptsAllowed: z.number().int().min(0),
  /** A liveness-checked template is stored (approve needs one). */
  hasTemplate: z.boolean(),
  attempts: z.array(AdminAttemptSchema),
})

export const AdminLocationSourceSchema = z.object({
  id: z.string(),
  ok: z.boolean(),
  error: z.string().nullable(),
  city: z.string().nullable(),
  countryIso2: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  /** Distance from the declared city (km, rounded), null without a point or a city. */
  distanceKm: z.number().nullable(),
  networkType: z.string().nullable(),
  flags: z.record(z.string(), z.boolean()),
})

export const AdminDeviceFixSchema = z.object({
  kind: z.enum(['fix', 'refused']),
  at: IsoDateTimeSchema,
  positionAt: nullableDate,
  placeId: z.number().int().nullable(),
  accuracyM: z.number().nullable(),
  timeZone: z.string().nullable(),
  refusal: z.string().nullable(),
  measure: DeviceMeasureSchema.nullable(),
  /** The device verdict under the policy in force when the fix arrived. */
  fixVerdict: z.object({ level: z.string(), verdict: z.string(), why: z.string() }).nullable(),
  diagnostics: DeviceDiagnosticsSchema.nullable(),
})
export type AdminDeviceFix = z.infer<typeof AdminDeviceFixSchema>

export const AdminLocationPanelSchema = z.object({
  declared: z
    .object({ name: z.string(), admin1: z.string().nullable(), country: z.string(), lat: z.number(), lng: z.number() })
    .nullable(),
  cityNote: z.string().nullable(),
  confidence: z.number().nullable(),
  divergence: z.boolean().nullable(),
  divergenceKm: z.number().nullable(),
  /** "IP located in Haifa, Israel, 112 km from the declared city" (red) or "matches" (green). */
  sentence: z.string(),
  tone: z.enum(['match', 'mismatch', 'unknown']),
  ipPoint: z.object({ lat: z.number(), lng: z.number() }).nullable(),
  sources: z.array(AdminLocationSourceSchema),
  gps: z.array(z.object({ lat: z.number(), lng: z.number(), accuracyM: z.number(), at: IsoDateTimeSchema })),
  /** The stored codes-only verdict of the decision (identity-document spec §8.7); null before a decision. */
  verdict: LocationVerdictSchema.nullable().default(null),
  /** Location v2: the member's current device answer (fix or refusal) with its fix-time verdict and diagnostics. */
  device: AdminDeviceFixSchema.nullable().default(null),
  observations: z.array(
    z.object({
      step: z.string(),
      ip: z.string(),
      ipTruncated: z.boolean(),
      flags: z.record(z.string(), z.boolean()).nullable(),
      at: IsoDateTimeSchema,
    })
  ),
})

export const AdminMediaInfoSchema = z.object({
  available: z.boolean(),
  /** Why there is nothing to show (the page says "No images available for this provider" for the first). */
  reason: z.enum(['provider_has_no_review_media', 'not_in_review', 'no_attempt']).optional(),
  /** `GET <path><n>` streams image n (0-based) while available. */
  path: z.string().optional(),
})

/**
 * Review media (docs/specs/2026-11-03-review-media-design.md §4, ruling R6): the photos held on our servers for a
 * person to decide a registration in review. Metadata only; `GET ADMIN_REVIEW_MEDIA_PATH` serves the bytes of one.
 * The list is empty unless the member's status is `review`.
 */
export const REVIEW_MEDIA_CONTENT_TYPE_IDS = ['image/jpeg', 'image/png', 'image/webp'] as const
export const AdminReviewMediaItemSchema = z.object({
  id: UuidSchema,
  kind: ReviewMediaKindSchema,
  contentType: z.enum(REVIEW_MEDIA_CONTENT_TYPE_IDS),
  byteLength: z.number().int().min(1),
  createdAt: IsoDateTimeSchema,
  /** Deleted at the decision, at the latest at this moment. */
  expiresAt: IsoDateTimeSchema,
})
export type AdminReviewMediaItem = z.infer<typeof AdminReviewMediaItemSchema>
export const AdminReviewMediaResponseSchema = z.object({ items: z.array(AdminReviewMediaItemSchema) })
export type AdminReviewMediaResponse = z.infer<typeof AdminReviewMediaResponseSchema>

/**
 * Why an action is not allowed (identity-document Task 30; the web maps each code to its own string): the state does
 * not allow it, the member has no usable template (`approval_needs_recapture`), an unresolved adverse document / face
 * reference reason (`approval_needs_manual_verification`: only "Verified manually" or a reject), an anchor that is
 * missing (`manual_route`), a document step the member cannot pass again (`document_manual_only`: sticky outcome, the
 * cap used up, or a document already scanned whose evidence is gone, ruling R30), or nothing for a video call to
 * resolve (`manual_verification_not_needed`).
 */
export const ADMIN_ACTION_BLOCK_CODES = [
  'invalid_state',
  'approval_needs_recapture',
  'approval_needs_manual_verification',
  'manual_route',
  'document_manual_only',
  'manual_verification_not_needed',
  'already_erased',
] as const
export const AdminActionBlockCodeSchema = z.enum(ADMIN_ACTION_BLOCK_CODES)
export type AdminActionBlockCode = z.infer<typeof AdminActionBlockCodeSchema>

const ActionStateSchema = z.object({
  allowed: z.boolean(),
  /** Why not, as a sentence an admin reads. */
  reason: z.string().optional(),
  /** Why not, as a stable code (set whenever `allowed` is false). */
  code: AdminActionBlockCodeSchema.optional(),
})
/** The single-approve confirmations the member's reasons call for (identity-document §13; R39 borderline). */
export const ADMIN_APPROVE_CONFIRMATIONS = ['confirmBorderline', 'confirmDocumentReview', 'confirmExemption'] as const
export const AdminMemberActionsSchema = z.object({
  approve: ActionStateSchema.extend({
    /** Allowed only with these ticks ("I reviewed the document result", "I reviewed the exemption", borderline). */
    confirmations: z.array(z.enum(ADMIN_APPROVE_CONFIRMATIONS)).default([]),
  }),
  reject: ActionStateSchema,
  newFaceCheck: ActionStateSchema.extend({
    /** "Ask for a new face check (no document needed)": the member is past the first decision with an anchor. */
    noDocumentNeeded: z.boolean().default(false),
    /** Before the first decision: the face check re-pins the member's current evidence unit (no new scan). */
    reusesDocument: z.boolean().default(false),
  }),
  /**
   * "Verified manually" / "Verify by video call" (identity-document §10.3, §13): resolves every `document_*` and
   * `face_reference_*` reason, works without a template; `approves` when no other reason is left.
   */
  verifyManually: ActionStateSchema.extend({
    resolves: z.array(z.string()).default([]),
    approves: z.boolean().default(false),
  }),
  unblock: ActionStateSchema,
  erase: ActionStateSchema,
})

/* ------------------------------------------------------------------ identity document panel (spec §13) */

/**
 * One reason a document was not approved automatically (api documents/auto-approval.ts): a stable code
 * (`check_failed:<check>` / `check_not_passed:<check>` name the check), with the codes, state, band, cause or the
 * value and floor it concerns. `notRecorded`: the input was not recorded for this member, so this line is unknown.
 * `not_recorded` alone: nothing found now, the reason was not recorded at the time.
 */
export const AdminApprovalBlockerSchema = z.object({
  code: z.string(),
  reasons: z.array(z.string()).optional(),
  state: CheckStateSchema.optional(),
  band: z.enum(['match', 'document_face_uncertain', 'document_face_mismatch']).nullable().optional(),
  cause: z.enum(['document', 'not_recognised', 'liveness_method', 'combination']).optional(),
  value: z.number().nullable().optional(),
  floor: z.number().optional(),
  notRecorded: z.boolean().optional(),
})
export type AdminApprovalBlocker = z.infer<typeof AdminApprovalBlockerSchema>

/**
 * Member detail, Document panel (identity-document spec §13, §9): the decision record, the shortened name with its
 * script, provenance and the member's answer, the per-check states, the face band and score, the reasons, provider,
 * versions, checked day, the exemption record, the anchor, the manual verification record. Never an image, the full
 * name, a document number, a birth date, the issuer or class of the member's document, or a link to the provider.
 */
export const AdminDocumentPanelSchema = z.object({
  /** The policy says `required` and the member has no first decision yet. */
  requirementApplies: z.boolean(),
  /**
   * Where the details come from: the first decision's record (`decision`), the member's current evidence unit before
   * the first decision (`evidence`), or nothing (`null`: no document result, or deleted at withdrawal).
   */
  source: z.enum(['decision', 'evidence']).nullable(),
  basis: DocumentCheckSchema.shape.basis.nullable(),
  status: DocumentCheckSchema.shape.status.nullable(),
  method: DocumentCheckSchema.shape.method,
  /** The UTC day the document was checked (`YYYY-MM-DD`). */
  checkedDay: z.string().nullable(),
  policyVersion: z.number().int().nullable(),
  /** The shortened display name only (rendered inside `<bdi>`); null while only a typed full name exists. */
  name: z
    .object({
      display: z.string(),
      script: NameScriptSchema.nullable(),
      provenance: z.enum(['printed', 'transliterated', 'typed', 'manual']).nullable(),
      answer: z.enum(['confirmed', 'disputed']).nullable(),
    })
    .nullable(),
  providerDecision: z.enum(['accept', 'review']).nullable(),
  /**
   * The 4-digit birth year of the scanned document (owner ruling 2026-10-29: the year only, never the full date).
   * Null before the first decision (an in-flight unit is never read for it) and for details sealed without it.
   */
  birthYear: z.number().int().nullable(),
  checks: z.record(z.string(), CheckStateSchema).nullable(),
  /** The document portrait vs the decisive liveness (spec §5.1); band null: not compared. */
  face: z
    .object({
      band: z.enum(['match', 'document_face_uncertain', 'document_face_mismatch']).nullable(),
      score: z.number().nullable(),
    })
    .nullable(),
  reasons: z.object({
    /** `document_*` reasons among the member's review reasons. */
    document: z.array(z.string()),
    /** `face_reference_*` reasons among the member's review reasons. */
    anchor: z.array(z.string()),
    /** Unresolved sticky reasons (`members.document_sticky`). */
    sticky: z.array(z.string()),
  }),
  provider: z.string().nullable(),
  providerName: z.string().nullable(),
  versions: z
    .object({
      policy: z.number().int().nullable(),
      consent: z.string().nullable(),
      bands: z.string().nullable(),
      qualityProfile: z.string().nullable(),
      model: z.string().nullable(),
    })
    .nullable(),
  /** The evaluation record of the assurance-matrix row that allowed an automatic approval (none at launch). */
  matrixRow: z.string().nullable(),
  /**
   * Whether the first decision approved the document automatically, and if not why (ruling 2026-11-03 Q2): computed
   * when read from the decided details and the member row. Null without a decided document result.
   */
  automaticApproval: z
    .object({
      given: z.boolean(),
      /** The matrix row's evaluation record when given. */
      matrixRecord: z.string().nullable(),
      blockers: z.array(AdminApprovalBlockerSchema),
    })
    .nullable(),
  exemption: z
    .object({
      state: z.enum(DOCUMENT_EXEMPTION_STATES),
      /** The policy key of the rule (ISO3 of the declared country). */
      rule: z.string(),
      level: z.enum(LOCATION_LEVELS),
      policyVersion: z.number().int(),
      /** The exemption conditions (spec §6.2) and whether each held. */
      conditions: z.record(z.string(), z.boolean()),
      evaluatedAt: z.string(),
    })
    .nullable(),
  anchor: z
    .object({
      state: z.enum(['present', 'missing']),
      modelId: z.string().nullable(),
      version: z.number().int(),
      /** `pending` (face-first spec §4.2): the face decision is stored, the document step still open. */
      basis: z.enum([...DocumentCheckSchema.shape.basis.options, 'pending']),
    })
    .nullable(),
  manual: ManualVerificationSchema.omit({ v: true })
    .extend({
      /** The reviewing admin's name (null once that admin is gone). */
      reviewerName: z.string().nullable(),
    })
    .nullable(),
  consent: z.object({ at: nullableDate, version: z.string().nullable(), provider: z.string().nullable() }),
  /** The member's evidence unit before the first decision (ruling R30: a scan whose evidence is gone). */
  evidence: z.object({
    current: z.boolean(),
    expiresAt: nullableDate,
    /** A document scan of this registration gave a usable result (an evidence unit was written). */
    scanned: z.boolean(),
  }),
  attempts: z.object({ counted: z.number().int().min(0), max: z.number().int().min(0) }),
})
export type AdminDocumentPanel = z.infer<typeof AdminDocumentPanelSchema>

export const AdminMemberDetailSchema = z.object({
  member: z.object({
    id: UuidSchema,
    name: z.string().nullable(),
    email: z.string().nullable(),
    stage: RegistrationStageSchema,
    status: RegistrationStatusValueSchema.nullable(),
    provider: z.string(),
    providerName: z.string(),
    /**
     * Passwordless (2026-11-03 spec §1, P9): how the voter signs in (`members.sign_in_mode`): `email` (link or code),
     * `password_or_email`, `password_and_email`.
     */
    signInMode: SignInModeSchema,
    extraAttempts: z.number().int(),
    reviewReasons: z.array(z.string()),
    rejectReason: z.string().nullable(),
    consentPrivacyAt: nullableDate,
    consentBiometricAt: nullableDate,
    consentBiometricProvider: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    emailVerifiedAt: nullableDate,
    locationSetAt: nullableDate,
    faceStartedAt: nullableDate,
    decidedAt: nullableDate,
    erasedAt: nullableDate,
  }),
  biometric: AdminBiometricPanelSchema,
  location: AdminLocationPanelSchema,
  media: AdminMediaInfoSchema,
  /** Review media held for the member (empty unless in review): whether there are photos, and their metadata. */
  reviewMedia: AdminReviewMediaResponseSchema,
  actions: AdminMemberActionsSchema,
  /** Identity document (spec §13); null for an erased member. */
  document: AdminDocumentPanelSchema.nullable(),
  groups: z.array(z.object({ id: UuidSchema, name: z.string() })),
  messages: z.array(MemberMessageSchema),
})
export type AdminMemberDetail = z.infer<typeof AdminMemberDetailSchema>

/* ------------------------------------------------------------------ review actions */

/** What an admin reject can give (the member card also knows 'duplicate'). */
export const ADMIN_REJECT_CATEGORIES = ['age', 'place', 'face', 'other'] as const
export const AdminApproveBodySchema = z.strictObject({
  confirmBorderline: z.boolean().optional(),
  /** identity-document §13: "I reviewed the document result" (a `document_review_required` registration). */
  confirmDocumentReview: z.boolean().optional(),
  /** identity-document §13: "I reviewed the exemption" (a `document_exempt` registration). */
  confirmExemption: z.boolean().optional(),
})
export const AdminRejectBodySchema = z.strictObject({
  reasonCategory: z.enum(ADMIN_REJECT_CATEGORIES),
  alsoBlock: z.boolean().optional(),
})
/**
 * "Verified manually" (identity-document §10.3, §13): the admin verified the person and the original document in a
 * supervised video call (`videoCall: true`) and may confirm the shortened name (`name.display`; a full name is
 * shortened by the server, spec §12.3).
 */
export const AdminVerifyManuallyBodySchema = z.strictObject({
  videoCall: z.literal(true),
  name: z.strictObject({ display: z.string().trim().min(1).max(120) }).optional(),
})
export type AdminVerifyManuallyBody = z.infer<typeof AdminVerifyManuallyBodySchema>
export const AdminActionResponseSchema = z.object({ ok: z.literal(true), status: z.string().nullable() })
export type AdminActionResponse = z.infer<typeof AdminActionResponseSchema>

/**
 * Bulk review actions (R112): the single-member actions applied to each selected member in turn, each in its own
 * transaction with the same guards. A member whose state does not allow the action is skipped (never forced: a
 * borderline approve is skipped as `needs_confirmation`). Erase needs `confirm: true`; reject needs a reason.
 */
export const ADMIN_BULK_ACTIONS = ['approve', 'reject', 'newFaceCheck', 'unblock', 'erase'] as const
export type AdminBulkAction = (typeof ADMIN_BULK_ACTIONS)[number]
export const ADMIN_BULK_MAX = 200

export const AdminBulkBodySchema = z
  .strictObject({
    action: z.enum(ADMIN_BULK_ACTIONS),
    memberIds: z.array(UuidSchema).min(1).max(ADMIN_BULK_MAX),
    /** Reject only (required there). */
    reasonCategory: z.enum(ADMIN_REJECT_CATEGORIES).optional(),
    /** Reject only: "Also block this email". */
    alsoBlock: z.boolean().optional(),
    /** Erase only (required there): the admin typed the confirmation. */
    confirm: z.boolean().optional(),
  })
  .superRefine((b, ctx) => {
    if (b.action === 'reject' && !b.reasonCategory)
      ctx.addIssue({ code: 'custom', path: ['reasonCategory'], message: 'Choose a reason to reject' })
    if (b.action === 'erase' && b.confirm !== true)
      ctx.addIssue({ code: 'custom', path: ['confirm'], message: 'Erasing needs an explicit confirmation' })
  })
export type AdminBulkBody = z.input<typeof AdminBulkBodySchema>

export const AdminBulkResultSchema = z.object({
  id: UuidSchema,
  ok: z.boolean(),
  /** The member's status after the action (ok), or its current status (skipped; null = not decided yet). */
  status: RegistrationStatusValueSchema.nullable(),
  /**
   * Why it was skipped: the error code of the single action (`invalid_state_transition`, `needs_confirmation`,
   * `approval_needs_recapture`, `uniqueness_conflict`, and the document rules of the shared approval service
   * `approval_needs_manual_verification` / `needs_document_confirmation`; a new face check `manual_route` /
   * `document_manual_only`…).
   */
  skipped: z.string().optional(),
  /** Skipped only: the member's name, so the summary can list who was left out. */
  name: z.string().nullable().optional(),
})
export type AdminBulkResult = z.infer<typeof AdminBulkResultSchema>

export const AdminBulkResponseSchema = z.object({
  action: z.enum(ADMIN_BULK_ACTIONS),
  applied: z.number().int().min(0),
  skipped: z.number().int().min(0),
  results: z.array(AdminBulkResultSchema),
})
export type AdminBulkResponse = z.infer<typeof AdminBulkResponseSchema>

/* ------------------------------------------------------------------ map, inbox, blocklist, audit */

export const AdminMapPointSchema = z.object({
  id: UuidSchema,
  status: RegistrationStatusValueSchema.nullable(),
  /** Declared city plus a deterministic jitter of at most 0.01°. */
  lat: z.number(),
  lng: z.number(),
})
export const AdminMapResponseSchema = z.object({ points: z.array(AdminMapPointSchema) })
export type AdminMapResponse = z.infer<typeof AdminMapResponseSchema>

export const AdminThreadSchema = z.object({
  memberId: UuidSchema,
  memberName: z.string().nullable(),
  memberEmail: z.string().nullable(),
  lastMessageAt: IsoDateTimeSchema,
  lastDirection: z.enum(['member', 'admin']),
  /** First 200 characters of the last message. */
  preview: z.string(),
  messageCount: z.number().int().min(0),
  /** Member messages not read by an admin yet (a reply marks them read). */
  unread: z.number().int().min(0),
})
export const AdminMessagesResponseSchema = z.object({ threads: z.array(AdminThreadSchema) })
export type AdminMessagesResponse = z.infer<typeof AdminMessagesResponseSchema>
export const AdminReplyBodySchema = z.strictObject({ body: z.string().trim().min(1).max(5000) })

export const AdminBlocklistItemSchema = z.object({
  id: UuidSchema,
  reason: z.enum(['duplicate', 'admin']),
  createdAt: IsoDateTimeSchema,
  unblockedAt: nullableDate,
  /** The blocked registration (null once erased). */
  member: z.object({ id: UuidSchema, status: z.string().nullable() }).nullable(),
  matchedMember: z.object({ id: UuidSchema, name: z.string().nullable() }).nullable(),
})
export const AdminBlocklistResponseSchema = z.object({ items: z.array(AdminBlocklistItemSchema) })
export type AdminBlocklistResponse = z.infer<typeof AdminBlocklistResponseSchema>

export const AdminAuditQuerySchema = PaginationQuerySchema.extend({
  action: z.string().max(64).optional(),
})
export const AdminAuditItemSchema = z.object({
  id: z.number().int(),
  adminId: UuidSchema.nullable(),
  adminName: z.string().nullable(),
  action: z.string(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  before: z.record(z.string(), z.unknown()).nullable(),
  after: z.record(z.string(), z.unknown()).nullable(),
  meta: z.record(z.string(), z.unknown()).nullable(),
  at: IsoDateTimeSchema,
})
export const AdminAuditResponseSchema = paginated(AdminAuditItemSchema)
export type AdminAuditResponse = z.infer<typeof AdminAuditResponseSchema>

/* ------------------------------------------------------------------ overview */

export const PROVIDER_ACTION_STATUSES = ['open', 'requested', 'confirmed', 'unresolved'] as const
export const AdminProviderActionSchema = z.object({
  id: UuidSchema,
  provider: z.string(),
  providerName: z.string(),
  kind: z.string(),
  refs: z.array(z.string()),
  status: z.enum(PROVIDER_ACTION_STATUSES),
  openedAt: IsoDateTimeSchema,
  requestedAt: nullableDate,
  closedAt: nullableDate,
  note: z.string().nullable(),
  /** open or requested for more than 30 days. */
  overdue: z.boolean(),
})
export const AdminProviderActionBodySchema = z.strictObject({
  status: z.enum(['requested', 'confirmed', 'unresolved']),
  note: z.string().trim().max(1000).optional(),
})

export const AdminOverviewSchema = z.object({
  /** Members by status; `in_progress` = not decided yet. */
  byStatus: z.record(z.string(), z.number().int().min(0)),
  byProvider: z.array(
    z.object({
      provider: z.string(),
      providerName: z.string(),
      total: z.number().int().min(0),
      byStatus: z.record(z.string(), z.number().int().min(0)),
    })
  ),
  /** Registrations created per day, the last 30 days (UTC days with at least one). */
  registrationsPerDay: z.array(z.object({ day: z.string(), count: z.number().int().min(0) })),
  /** Decisions taken per day, the last 30 days (UTC days with at least one approved or rejected). */
  decisionsPerDay: z.array(
    z.object({ day: z.string(), approved: z.number().int().min(0), rejected: z.number().int().min(0) })
  ),
  reviewQueue: z.number().int().min(0),
  /** OF-7: member messages newer than the requesting admin's `messagesSeenAt`; 0 when the admin never opened Messages. */
  unreadMessages: z.number().int().min(0),
  providers: z.array(
    z.object({
      id: z.string(),
      displayName: z.string(),
      effective: z.boolean(),
      /** healthCheck, cached 5 minutes; null when the provider is not configured. */
      health: z
        .object({
          ok: z.boolean(),
          detail: z.string(),
          credits: z
            .object({ credit: z.number().optional(), quota: z.number().optional(), balanceUsd: z.number().optional() })
            .optional(),
          checkedAt: IsoDateTimeSchema,
        })
        .nullable(),
      /** Attempts started this calendar month and their estimated cost (null price ⇒ unknown). */
      month: z.object({ attempts: z.number().int().min(0), estimatedCostUsd: z.number().nullable() }),
    })
  ),
  /** "N provider deletions need attention": stuck or overdue provider deletions and support actions. */
  deletionsNeedingAttention: z.number().int().min(0),
  overdueDeletions: z.array(
    z.object({
      kind: z.enum(['ref', 'action']),
      id: UuidSchema,
      provider: z.string(),
      reason: z.enum(['stuck', 'late']),
      dueAt: nullableDate,
    })
  ),
  providerActions: z.array(AdminProviderActionSchema),
  /**
   * Identity-document provider sessions still not confirmed deleted 7 days after their deletion was requested
   * (spec identity-document §11, A18); also counted in `deletionsNeedingAttention`.
   */
  documentDeletionsOverdue: z.number().int().min(0),
})
export type AdminOverview = z.infer<typeof AdminOverviewSchema>

/* ------------------------------------------------------------------ provider comparison (P5) */

export const AdminCompareQuerySchema = z.object({
  /** Inclusive start (default: 90 days ago). */
  from: IsoDateTimeSchema.optional(),
  /** Exclusive end (default: now). */
  to: IsoDateTimeSchema.optional(),
})

export const FUNNEL_STEPS = ['started', 'emailVerified', 'locationSet', 'faceStarted', 'decided'] as const
const counts = z.record(z.string(), z.number().int().min(0))

export const ProviderComparisonSchema = z.object({
  provider: z.string(),
  providerName: z.string(),
  removed: z.boolean(),
  /** Test providers (code TEST_PROVIDER): "Test (staff only), not members". */
  testProvider: z.boolean(),
  assurance: z.string().nullable(),
  funnel: z.object({
    started: z.number().int(),
    emailVerified: z.number().int(),
    locationSet: z.number().int(),
    faceStarted: z.number().int(),
    decided: z.number().int(),
  }),
  /** Registrations lost between a step and the one before it. */
  dropOff: z.object({
    emailVerified: z.number().int(),
    locationSet: z.number().int(),
    faceStarted: z.number().int(),
    decided: z.number().int(),
  }),
  medianFaceStepSeconds: z.number().nullable(),
  /** Current status of the decided registrations (`erased` for the erased ones). */
  outcomes: z.object({
    approved: z.number().int(),
    review: z.number().int(),
    retry_allowed: z.number().int(),
    blocked_duplicate: z.number().int(),
    rejected: z.number().int(),
    erased: z.number().int(),
  }),
  /** Decided attempts (live + erased members' attempt_stats). */
  decidedAttempts: z.number().int(),
  /** Ten bins: 0–9, 10–19, …, 90–100. */
  scoreHistogram: z.array(z.number().int()),
  /** Share of decided attempts with a numeric liveness score (null when there is none). */
  numericScoreShare: z.number().nullable(),
  methods: counts,
  outcomeCodes: counts,
  reviewReasons: counts,
  ageGap: z.object({ n: z.number().int(), meanYears: z.number().nullable(), medianYears: z.number().nullable() }),
  cost: z.object({
    pricePerAttempt: z.number().nullable(),
    /** Attempts that reached the provider. */
    attempts: z.number().int(),
    /** null = unknown (no price quoted). */
    estimatedUsd: z.number().nullable(),
  }),
  /** (counted attempts − members with one) / members with one. */
  retriesPerMember: z.number().nullable(),
})
export type ProviderComparison = z.infer<typeof ProviderComparisonSchema>

export const AdminCompareResponseSchema = z.object({
  from: IsoDateTimeSchema,
  to: IsoDateTimeSchema,
  providers: z.array(ProviderComparisonSchema),
  /** Registrations per UTC day and provider over the period (days and providers with at least one). */
  perDay: z.array(
    z.object({
      day: z.string(),
      provider: z.string(),
      started: z.number().int().min(0),
      approved: z.number().int().min(0),
      rejected: z.number().int().min(0),
      review: z.number().int().min(0),
    })
  ),
})
export type AdminCompareResponse = z.infer<typeof AdminCompareResponseSchema>

/* ------------------------------------------------------------------ non-JSON endpoints */

/** `GET /api/admin/members/:id/media/:n` (E9): image bytes, `no-store`. */
export const ADMIN_MEDIA_PATH = '/api/admin/members/:id/media/:n'
/**
 * `GET /api/admin/members/:id/review-media/:mediaId` (review-media spec §4): the bytes of one held photo, only while
 * the member is in review; `no-store`, inline, audited per view (`review_media_view`).
 */
export const ADMIN_REVIEW_MEDIA_PATH = '/api/admin/members/:id/review-media/:mediaId'
export const adminReviewMediaUrl = (memberId: string, mediaId: string) =>
  `/api/admin/members/${encodeURIComponent(memberId)}/review-media/${encodeURIComponent(mediaId)}`
/** `GET /api/admin/compare.csv?from&to` (P5): text/csv, `no-store`. */
export const ADMIN_COMPARE_CSV_PATH = '/api/admin/compare.csv'
/** `GET /api/admin/calibration.csv` (E5): text/csv, `no-store`, header `bin_low,bin_high,impostor_count,genuine_count`. */
export const ADMIN_CALIBRATION_CSV_PATH = '/api/admin/calibration.csv'

/* ------------------------------------------------------------------ routes */

const MemberIdParamsSchema = z.object({ id: UuidSchema })

export const adminRoutes = {
  adminMembers: route({
    method: 'GET',
    path: '/api/admin/members',
    auth: 'admin',
    query: AdminMembersQuerySchema,
    response: AdminMembersResponseSchema,
  }),
  adminMember: route({
    method: 'GET',
    path: '/api/admin/members/:id',
    auth: 'admin',
    params: MemberIdParamsSchema,
    response: AdminMemberDetailSchema,
  }),
  /** Review media (review-media spec §4, R6): the photos held for the review, metadata only; 404 unless in review. */
  adminMemberReviewMedia: route({
    method: 'GET',
    path: '/api/admin/members/:id/review-media',
    auth: 'admin',
    params: MemberIdParamsSchema,
    response: AdminReviewMediaResponseSchema,
  }),
  adminMemberApprove: route({
    method: 'POST',
    path: '/api/admin/members/:id/approve',
    auth: 'admin',
    params: MemberIdParamsSchema,
    body: AdminApproveBodySchema,
    response: AdminActionResponseSchema,
  }),
  adminMemberReject: route({
    method: 'POST',
    path: '/api/admin/members/:id/reject',
    auth: 'admin',
    params: MemberIdParamsSchema,
    body: AdminRejectBodySchema,
    response: AdminActionResponseSchema,
  }),
  adminMemberNewFaceCheck: route({
    method: 'POST',
    path: '/api/admin/members/:id/new-face-check',
    auth: 'admin',
    params: MemberIdParamsSchema,
    response: AdminActionResponseSchema,
  }),
  adminMemberVerifyManually: route({
    method: 'POST',
    path: '/api/admin/members/:id/verify-manually',
    auth: 'admin',
    params: MemberIdParamsSchema,
    body: AdminVerifyManuallyBodySchema,
    response: AdminActionResponseSchema,
  }),
  adminMemberUnblock: route({
    method: 'POST',
    path: '/api/admin/members/:id/unblock',
    auth: 'admin',
    params: MemberIdParamsSchema,
    response: AdminActionResponseSchema,
  }),
  adminMemberErase: route({
    method: 'POST',
    path: '/api/admin/members/:id/erase',
    auth: 'admin',
    params: MemberIdParamsSchema,
    response: AdminActionResponseSchema,
  }),
  adminMembersBulk: route({
    method: 'POST',
    path: '/api/admin/members/bulk',
    auth: 'admin',
    body: AdminBulkBodySchema,
    response: AdminBulkResponseSchema,
  }),
  adminDiscarded: route({
    method: 'GET',
    path: '/api/admin/discarded',
    auth: 'admin',
    query: AdminDiscardedQuerySchema,
    response: AdminDiscardedResponseSchema,
  }),
  adminReviewQueue: route({
    method: 'GET',
    path: '/api/admin/review',
    auth: 'admin',
    response: AdminReviewResponseSchema,
  }),
  adminMap: route({ method: 'GET', path: '/api/admin/map', auth: 'admin', response: AdminMapResponseSchema }),
  adminMessages: route({
    method: 'GET',
    path: '/api/admin/messages',
    auth: 'admin',
    response: AdminMessagesResponseSchema,
  }),
  adminMessagesSeen: route({
    method: 'POST',
    path: '/api/admin/messages/seen',
    auth: 'admin',
    response: z.object({ ok: z.literal(true) }),
  }),
  adminMemberReply: route({
    method: 'POST',
    path: '/api/admin/members/:id/messages',
    auth: 'admin',
    params: MemberIdParamsSchema,
    body: AdminReplyBodySchema,
    response: z.object({ message: MemberMessageSchema }),
  }),
  adminBlocklist: route({
    method: 'GET',
    path: '/api/admin/blocklist',
    auth: 'admin',
    response: AdminBlocklistResponseSchema,
  }),
  adminBlocklistUnblock: route({
    method: 'POST',
    path: '/api/admin/blocklist/:id/unblock',
    auth: 'admin',
    params: z.object({ id: UuidSchema }),
    response: OkResponseSchema,
  }),
  adminAudit: route({
    method: 'GET',
    path: '/api/admin/audit',
    auth: 'admin',
    query: AdminAuditQuerySchema,
    response: AdminAuditResponseSchema,
  }),
  adminOverview: route({
    method: 'GET',
    path: '/api/admin/overview',
    auth: 'admin',
    response: AdminOverviewSchema,
  }),
  adminProviderAction: route({
    method: 'POST',
    path: '/api/admin/provider-actions/:id',
    auth: 'admin',
    params: z.object({ id: UuidSchema }),
    body: AdminProviderActionBodySchema,
    response: z.object({ action: AdminProviderActionSchema }),
  }),
  adminCompare: route({
    method: 'GET',
    path: '/api/admin/compare',
    auth: 'admin',
    query: AdminCompareQuerySchema,
    response: AdminCompareResponseSchema,
  }),
} as const
